import { Loader2, Undo2 } from 'lucide-react'
import clsx from 'clsx'
import { Tooltip } from '@/components/Tooltip'
import type { DiffChange } from '@/types/diff'
import { humanizePath } from '../pathLabel'

export interface DiffChangeRowProps {
  anchorId: string
  change: DiffChange
  errorMessage?: string
  highlighted?: boolean
  onRevert?: () => void
  /** A revert control would otherwise apply here, but this field's value is
   * masked (e.g. a client's API key) - reverting a mask would just PUT the
   * literal placeholder string back, which PRD 21's unmask-on-write logic
   * reads as "leave unchanged," not a real revert. Rendered as a disabled
   * icon with an explanation instead of silently vanishing. */
  maskedNoRevert?: boolean
  reverting?: boolean
}

// Formats a leaf value for display without ever falling back to raw JSON
// syntax (PRD 07 AC1) - the diff walk (backend PRD 23) emits true scalar
// leaves in the common case, but a key present on only one side of an
// otherwise-modified file surfaces as a single entry whose value can still
// be a nested object/array (the parallel walk can't descend further once
// one side lacks the branch entirely) - rendered here as a plain,
// bracket-free list rather than JSON.stringify.
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(formatValue).join(', ')
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, entryValue]) => `${key}: ${formatValue(entryValue)}`)
      .join(', ')
  }
  return String(value)
}

export function DiffChangeRow({
  anchorId,
  change,
  errorMessage,
  highlighted,
  onRevert,
  maskedNoRevert,
  reverting,
}: DiffChangeRowProps) {
  return (
    <div
      id={anchorId}
      className={clsx(
        'flex items-start justify-between gap-3 rounded-control border px-3 py-2 transition-colors',
        highlighted ? 'border-accent bg-accent/5' : 'border-border',
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="text-meta font-medium text-text-secondary">{humanizePath(change.path)}</span>
        <div className="flex flex-wrap items-center gap-2 text-body">
          <span className="text-danger line-through">{formatValue(change.oldValue)}</span>
          <span className="text-text-disabled">→</span>
          <span className="text-success">{formatValue(change.newValue)}</span>
        </div>
        {errorMessage && <span className="text-meta text-danger">{errorMessage}</span>}
      </div>
      {onRevert && (
        <Tooltip text="Revert this field to its live value">
          <button
            type="button"
            aria-label={`Revert ${humanizePath(change.path)} to its live value`}
            disabled={reverting}
            onClick={onRevert}
            className="shrink-0 text-text-secondary hover:text-accent disabled:cursor-wait disabled:text-text-disabled"
          >
            {reverting ? (
              <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
            ) : (
              <Undo2 size={14} strokeWidth={1.5} />
            )}
          </button>
        </Tooltip>
      )}
      {maskedNoRevert && (
        <Tooltip text="Masked values can't be reverted one at a time. Use Discard changes to undo the whole file.">
          <span tabIndex={0} className="shrink-0 text-text-disabled">
            <Undo2 size={14} strokeWidth={1.5} />
          </span>
        </Tooltip>
      )}
    </div>
  )
}
